// app/frontend/components/inbox/MessageComposer.tsx

import { useRef, useEffect, useLayoutEffect, useCallback, useState } from 'react'
import { Send, Languages, Loader2, Paperclip, Smile, Type, Image as ImageIcon } from 'lucide-react'
import type { Editor } from '@tiptap/react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import {
  MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_DISPLAY,
  isAllowedFileType,
  isFileSizeAllowed,
  formatFileSize,
  extractPastedFile
} from '@/lib/fileUpload'
import type { VisitorLanguage } from '@/types/conversation'
import { EMOJIS } from '@/lib/constants'
import { useEmojiShortcode } from '@/hooks/useEmojiShortcode'
import { useGifShortcode } from '@/hooks/useGifShortcode'
import { EmojiShortcodePopover } from './EmojiShortcodePopover'
import { SuggestionBar, type Suggestion, type AiDraftSuggestion } from './SuggestionBar'
import { RichTextEditor } from './RichTextEditor'
import { RichToolbar } from './RichToolbar'
import { GifPicker } from './GifPicker'
import { useBooleanEntitlement } from '@/hooks/useEntitlement'
import type { PendingFile } from '@/hooks/useFileUpload'
import { PendingAttachment } from './PendingAttachment'

interface MessageComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
  onTyping?: (isTyping: boolean) => void
  isSending: boolean
  isTranslating: boolean
  isDisabled: boolean
  visitorLanguage: VisitorLanguage | null
  translateOnSend: boolean
  onToggleTranslate: () => void
  onReopen: () => void
  pendingFile?: PendingFile | null
  onFileSelect?: (file: File) => void
  onFileRemove?: () => void
  onFileRetry?: () => void
  suggestions?: Suggestion[]
  allSuggestions?: Suggestion[]
  aiDraft?: AiDraftSuggestion | null
  onPickSuggestion?: (s: Suggestion) => void
  onPickDraft?: (d: AiDraftSuggestion) => void
  onSendGif?: (url: string, alt: string) => void
}

const TYPING_TIMEOUT_MS = 3000

export function MessageComposer({
  value,
  onChange,
  onSubmit,
  onTyping,
  isSending,
  isTranslating,
  isDisabled,
  visitorLanguage,
  translateOnSend,
  onToggleTranslate,
  onReopen,
  pendingFile,
  onFileSelect,
  onFileRemove,
  onFileRetry,
  suggestions = [],
  allSuggestions = [],
  aiDraft = null,
  onPickSuggestion,
  onPickDraft,
  onSendGif
}: MessageComposerProps) {
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTypingRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [textareaFocused, setTextareaFocused] = useState(false)
  const [richMode, setRichMode] = useState(false)
  const [editor, setEditor] = useState<Editor | null>(null)
  const [showGifPicker, setShowGifPicker] = useState(false)
  const { enabled: richTextEnabled } = useBooleanEntitlement('rich_text')
  const { enabled: gifPickerEnabled } = useBooleanEntitlement('gif_picker')

  const shortcode = useEmojiShortcode({ value, onChange, textareaRef })
  const gifShortcode = useGifShortcode({
    value,
    onChange,
    textareaRef,
    enabled: Boolean(gifPickerEnabled && onSendGif)
  })

  // Resize the textarea whenever the value changes (covers programmatic sets
  // from chip clicks, drafts, translation toggles — onInput only fires for
  // user typing).
  useLayoutEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
  }, [value])

  const insertEmoji = useCallback((emoji: string) => {
    onChange(value + emoji)
    setShowEmojiPicker(false)
  }, [value, onChange])

  // Replaces the textarea draft with chip body, auto-fills any {{*url*}}
  // placeholders from clipboard, then selects the first remaining {{var}}
  // so the agent can type to fill it.
  const insertSuggestionBody = useCallback(async (body: string) => {
    let final = body
    const urlPlaceholders = [...body.matchAll(/\{\{(\w*url\w*)\}\}/gi)]
    if (urlPlaceholders.length > 0) {
      try {
        const clip = (await navigator.clipboard.readText()).trim()
        if (/^https?:\/\/\S+$/.test(clip)) {
          urlPlaceholders.forEach(m => { final = final.replace(m[0], clip) })
        }
      } catch { /* clipboard read denied — fall through to manual selection */ }
    }

    onChange(final)

    requestAnimationFrame(() => {
      const ta = textareaRef.current
      if (!ta) return
      ta.focus()
      const next = final.match(/\{\{\w+\}\}/)
      if (next && next.index != null) {
        ta.setSelectionRange(next.index, next.index + next[0].length)
      } else {
        ta.setSelectionRange(final.length, final.length)
      }
    })
  }, [onChange])

  // Jump to the next placeholder when the agent presses Tab while one is selected.
  const advancePlaceholder = useCallback(() => {
    const ta = textareaRef.current
    if (!ta) return false
    const after = value.slice(ta.selectionEnd)
    const m = after.match(/\{\{\w+\}\}/)
    if (!m || m.index == null) return false
    const start = ta.selectionEnd + m.index
    ta.setSelectionRange(start, start + m[0].length)
    return true
  }, [value])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (!onFileSelect) return
    const file = extractPastedFile(e.clipboardData)
    if (!file) return

    e.preventDefault()

    if (!isAllowedFileType(file.type)) {
      toast.error('File type not supported', {
        description: `Allowed: ${ALLOWED_FILE_TYPES_DISPLAY}`
      })
      return
    }

    if (!isFileSizeAllowed(file.size)) {
      toast.error('File too large', {
        description: `Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`
      })
      return
    }

    onFileSelect(file)
  }, [onFileSelect])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !onFileSelect) return

    // Reset input so same file can be selected again
    e.target.value = ''

    // Validate file type
    if (!isAllowedFileType(file.type)) {
      toast.error('File type not supported', {
        description: `Allowed: ${ALLOWED_FILE_TYPES_DISPLAY}`
      })
      return
    }

    // Validate file size
    if (!isFileSizeAllowed(file.size)) {
      toast.error('File too large', {
        description: `Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`
      })
      return
    }

    onFileSelect(file)
  }, [onFileSelect])

  // Handle typing indicator with debounce
  const handleTypingChange = useCallback((newValue: string) => {
    onChange(newValue)

    if (!onTyping) return

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // If user typed something and wasn't already typing, signal typing started
    if (newValue.trim() && !isTypingRef.current) {
      isTypingRef.current = true
      onTyping(true)
    }

    // Set timeout to stop typing indicator after inactivity
    if (newValue.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        if (isTypingRef.current) {
          isTypingRef.current = false
          onTyping(false)
        }
      }, TYPING_TIMEOUT_MS)
    } else {
      // If input is empty, immediately stop typing indicator
      if (isTypingRef.current) {
        isTypingRef.current = false
        onTyping(false)
      }
    }
  }, [onChange, onTyping])

  // Clean up timeout and stop typing indicator on submit or unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [])

  // Stop typing indicator when message is sent
  const handleSubmit = useCallback((e: React.FormEvent) => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }
    if (isTypingRef.current && onTyping) {
      isTypingRef.current = false
      onTyping(false)
    }
    onSubmit(e)
  }, [onSubmit, onTyping])

  // Can submit if there's text OR a pending file. A send while the attachment
  // is still uploading waits for it; a failed one must be retried or removed
  // first so it's never silently dropped.
  const canSubmit = (value.trim() || pendingFile) && !isSending && !isTranslating && pendingFile?.status !== 'error'

  if (isDisabled) {
    return (
      <div className="border-t border-border bg-card p-4 shrink-0">
        <div className="text-center text-muted-foreground py-2">
          This conversation is resolved.{' '}
          <button
            onClick={onReopen}
            className="text-primary hover:underline cursor-pointer"
          >
            Mark as unresolved
          </button>{' '}
          to send a message.
        </div>
      </div>
    )
  }

  return (
    <div className="border-t border-border bg-card p-4 shrink-0">
      <div className="space-y-2">
        {visitorLanguage && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onToggleTranslate}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors cursor-pointer",
                translateOnSend
                  ? "bg-primary/20 text-primary"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              )}
            >
              <Languages className="size-3" />
              {translateOnSend
                ? `Translating to ${visitorLanguage.name}`
                : `Translate to ${visitorLanguage.name}`}
            </button>
            {translateOnSend && (
              <span className="text-xs text-muted-foreground">
                Your message will be translated before sending
              </span>
            )}
          </div>
        )}
        {pendingFile && (
          <PendingAttachment
            pendingFile={pendingFile}
            onRemove={isSending || isTranslating ? undefined : onFileRemove}
            onRetry={onFileRetry}
          />
        )}

        {/* Emoji picker */}
        {showEmojiPicker && (
          <div className="flex flex-wrap gap-1 p-2 bg-muted rounded-lg">
            {EMOJIS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => insertEmoji(emoji)}
                className="size-9 text-xl hover:bg-background rounded cursor-pointer flex items-center justify-center"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Suggested-answer chips above the textarea */}
        {(suggestions.length > 0 || aiDraft || allSuggestions.length > 0) && (
          <SuggestionBar
            suggestions={suggestions}
            allSuggestions={allSuggestions}
            aiDraft={aiDraft}
            showShortcuts={textareaFocused}
            onPickSuggestion={(s) => { void insertSuggestionBody(s.body); onPickSuggestion?.(s) }}
            onPickDraft={(d) => { void insertSuggestionBody(d.body); onPickDraft?.(d) }}
          />
        )}

        {/* Rich text editor — shown when formatting toggle is on */}
        {richTextEnabled && richMode && (
          <div className="bg-muted rounded-lg overflow-hidden">
            <RichToolbar editor={editor} />
            <RichTextEditor
              value={value}
              onChange={onChange}
              onEditorReady={setEditor}
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex gap-2 items-end relative">
          {/* `:` emoji shortcode popover */}
          {shortcode.active && (
            <EmojiShortcodePopover
              matches={shortcode.matches}
              selectedIndex={shortcode.selectedIndex}
              onSelect={shortcode.insert}
              onHover={shortcode.select}
            />
          )}

          {/* `/gif <query>` slash-command popover — feeds the trailing
              text into Giphy live, sends the picked GIF as a standalone
              message and clears the trigger. */}
          {gifShortcode.active && onSendGif && (
            <GifPicker
              query={gifShortcode.query}
              onClose={gifShortcode.close}
              onPick={(url, alt) => {
                onSendGif(url, alt)
                gifShortcode.close()
              }}
            />
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={handleFileChange}
          />

          {/* Attachment button */}
          {onFileSelect && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending || isTranslating}
              className="cursor-pointer shrink-0"
              title="Attach file"
            >
              <Paperclip className="size-4" />
            </Button>
          )}

          {/* Emoji button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={isSending || isTranslating}
            className={cn("cursor-pointer shrink-0", showEmojiPicker && "bg-muted")}
            title="Insert emoji"
          >
            <Smile className="size-4" />
          </Button>

          {/* GIF button (BC-23) — gated by `gif_picker` entitlement */}
          {gifPickerEnabled && onSendGif && (
            <div className="relative shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowGifPicker(o => !o)}
                disabled={isSending || isTranslating}
                className={cn('cursor-pointer', showGifPicker && 'bg-muted')}
                title="Insert GIF"
              >
                <ImageIcon className="size-4" />
              </Button>
              {showGifPicker && (
                <GifPicker
                  onClose={() => setShowGifPicker(false)}
                  onPick={(url, alt) => {
                    setShowGifPicker(false)
                    onSendGif(url, alt)
                  }}
                />
              )}
            </div>
          )}

          {/* Rich formatting toggle (BC-23) — gated by `rich_text` entitlement */}
          {richTextEnabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setRichMode(m => !m)}
              disabled={isSending || isTranslating}
              className={cn(
                "cursor-pointer shrink-0",
                richMode && "bg-primary/15 text-primary hover:bg-primary/20"
              )}
              title={richMode ? "Hide formatting" : "Show formatting"}
              aria-pressed={richMode}
            >
              <Type className="size-4" />
            </Button>
          )}

          {richTextEnabled && richMode ? (
            <div className="flex-1 text-xs text-muted-foreground self-center pl-2 truncate">
              Formatting on — write above
            </div>
          ) : (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => handleTypingChange(e.target.value)}
            onFocus={() => setTextareaFocused(true)}
            onBlur={() => setTextareaFocused(false)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              // /gif slash command — Escape clears the trigger
              if (gifShortcode.handleKeyDown(e)) return

              // Emoji shortcode popover swallows arrows / enter / tab / escape
              if (shortcode.handleKeyDown(e)) return

              // Tab jumps to the next {{placeholder}} in the draft
              if (e.key === 'Tab' && !e.shiftKey && advancePlaceholder()) {
                e.preventDefault()
                return
              }

              // Ctrl/Meta + digit picks the Nth suggestion (1-9)
              if ((e.metaKey || e.ctrlKey) && /^[1-9]$/.test(e.key)) {
                const idx = parseInt(e.key, 10) - 1
                const s = suggestions[idx]
                if (s) {
                  e.preventDefault()
                  void insertSuggestionBody(s.body)
                  onPickSuggestion?.(s)
                  return
                }
              }

              // Enter = send, Shift+Enter = newline
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (canSubmit) {
                  handleSubmit(e)
                }
              }
            }}
            placeholder={translateOnSend && visitorLanguage
              ? `Type in your language, will translate to ${visitorLanguage.name}...`
              : "Type your message..."}
            className="flex-1 bg-muted rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none min-h-[40px] max-h-[200px] overflow-y-auto"
            disabled={isSending || isTranslating}
            rows={1}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement
              target.style.height = 'auto'
              target.style.height = `${Math.min(target.scrollHeight, 200)}px`
            }}
          />
          )}
          <Button
            type="submit"
            disabled={!canSubmit}
            className="cursor-pointer shrink-0"
          >
            {isTranslating || isSending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default MessageComposer
